import { cn } from '@/lib/utils';

/**
 * Mic Icon Component
 *
 * A customizable microphone icon for audio recording
 *
 * @example
 * ```tsx
 * <Mic />
 * <Mic className="text-blue-500" size={24} />
 * <Mic fill="currentColor" width={24} height={24} />
 * <Mic className="hover:text-pranik-brand-dark cursor-pointer" onClick={handleRecord} />
 * ```
 */

export interface MicProps extends React.SVGProps<SVGSVGElement> {
  /**
   * Size of the icon (sets both width and height)
   * @default 24
   */
  size?: number;
  /**
   * Custom width (overrides size)
   */
  width?: number;
  /**
   * Custom height (overrides size)
   */
  height?: number;
  /**
   * Fill color for the icon
   * Use "currentColor" to inherit text color from parent
   * @default "#678AF6"
   */
  fill?: string;
  /**
   * Additional CSS classes
   */
  className?: string;
  /**
   * Accessible label for screen readers
   */
  'aria-label'?: string;
}

export function Mic({
  size = 24,
  width,
  height,
  fill = '#678AF6',
  className,
  'aria-label': ariaLabel,
  ...props
}: MicProps) {
  const w = width ?? size;
  const h = height ?? size;

  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width={w}
      height={h}
      viewBox="0 0 24 24"
      className={cn('inline-block aspect-square flex-shrink-0 transition-colors', className)}
      role={ariaLabel ? 'img' : 'presentation'}
      aria-label={ariaLabel}
      aria-hidden={!ariaLabel}
      {...props}
    >
      <path
        d="M12 14C11.1667 14 10.4583 13.7083 9.875 13.125C9.29167 12.5417 9 11.8333 9 11V5C9 4.16667 9.29167 3.45833 9.875 2.875C10.4583 2.29167 11.1667 2 12 2C12.8333 2 13.5417 2.29167 14.125 2.875C14.7083 3.45833 15 4.16667 15 5V11C15 11.8333 14.7083 12.5417 14.125 13.125C13.5417 13.7083 12.8333 14 12 14ZM11 21V17.925C9.26667 17.6917 7.83333 16.9167 6.7 15.6C5.56667 14.2833 5 12.75 5 11H7C7 12.3833 7.4875 13.5625 8.4625 14.5375C9.4375 15.5125 10.6167 16 12 16C13.3833 16 14.5625 15.5125 15.5375 14.5375C16.5125 13.5625 17 12.3833 17 11H19C19 12.75 18.4333 14.2833 17.3 15.6C16.1667 16.9167 14.7333 17.6917 13 17.925V21H11ZM12 12C12.2833 12 12.5208 11.9042 12.7125 11.7125C12.9042 11.5208 13 11.2833 13 11V5C13 4.71667 12.9042 4.47917 12.7125 4.2875C12.5208 4.09583 12.2833 4 12 4C11.7167 4 11.4792 4.09583 11.2875 4.2875C11.0958 4.47917 11 4.71667 11 5V11C11 11.2833 11.0958 11.5208 11.2875 11.7125C11.4792 11.9042 11.7167 12 12 12Z"
        fill={fill}
      />
    </svg>
  );
}
